import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import RouteGuard from './components/RouteGuard';
import UpdatePrompt from './components/UpdatePrompt';
import Login from './pages/Login';
import CompanyPicker from './pages/CompanyPicker';
import AuthCallback from './pages/AuthCallback';  // eslint-disable-line no-unused-vars
import Dashboard from './pages/Dashboard';
import Reports from './pages/Reports';
import ExecDashboardMIS from './pages/mis/ExecDashboard';
import ProjectDeepDiveMIS from './pages/mis/ProjectDeepDive';
import CompareProjectsMIS from './pages/mis/CompareProjects';
import ReportBuilderMIS from './pages/mis/ReportBuilder';
import ReportBuilder from './pages/reports/ReportBuilder';
import Settings from './pages/Settings';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import ProjectPlanningList from './pages/ProjectPlanningList';
import ProjectPlanningDetail from './pages/ProjectPlanningDetail';
import HindranceRegister from './pages/projects/HindranceRegister';
import ApprovalsTracker from './pages/projects/ApprovalsTracker';
import PrimaveraSync from './pages/projects/PrimaveraSync';
import SafetyDashboard from './pages/safety/SafetyDashboard';
import RaiseObservation from './pages/safety/RaiseObservation';
import RaiseNearMiss from './pages/safety/RaiseNearMiss';
import ObservationDetail from './pages/safety/ObservationDetail';
import NearMissDetail from './pages/safety/NearMissDetail';
import ToolboxTalks from './pages/safety/ToolboxTalks';
import PPECompliance from './pages/safety/PPECompliance';
import ComplianceDashboard from './pages/hr/ComplianceDashboard';
import ComplianceInsurance from './pages/hr/ComplianceInsurance';
import ComplianceStatutory from './pages/hr/ComplianceStatutory';
import ComplianceVault from './pages/hr/ComplianceVault';
import RecruitmentDashboard from './pages/hr/recruitment/RecruitmentDashboard';
import AttendanceReport from './pages/hr/AttendanceReport';
import JobOpenings from './pages/hr/recruitment/JobOpenings';
import JobDetail from './pages/hr/recruitment/JobDetail';
import CandidateDetail from './pages/hr/recruitment/CandidateDetail';
import JobBoard from './pages/hr/recruitment/JobBoard';
import CandidatePool from './pages/hr/recruitment/CandidatePool';
import ResumeScanner from './pages/hr/recruitment/ResumeScanner';
import JobMatches from './pages/hr/recruitment/JobMatches';
import PermitToWork from './pages/safety/PermitToWork';
import MobileSafetyHome from './pages/mobile/MobileSafetyHome';
import MobileQualityHome from './pages/mobile/MobileQualityHome';
import Procurement from './pages/Procurement';
import ProcurementDailyReport from './pages/ProcurementDailyReport';
import MaterialMaster from './pages/procurement/MaterialMaster';
import VendorMaster from './pages/procurement/VendorMaster';
import VendorPerformancePage from './pages/procurement/VendorPerformance';
import VendorChangeRequests from './pages/procurement/VendorChangeRequests';
import VendorInvoicesPage from './pages/finance/VendorInvoices';
import WorkOrders from './pages/procurement/WorkOrders';
import WorkOrderDetail from './pages/procurement/WorkOrderDetail';
import {
  PettyCashDashboard, ExpenseRequest, BillScanner, ApprovalsQueue,
  DailyCashClosure, VendorIntelligence, FraudAlerts, PettyCashReports,
  FinanceProcessingQueue,
} from './pages/petty_cash/PettyCash';
import CashAdvances from './pages/petty_cash/CashAdvances';
import OverviewFraudAlerts from './pages/overview/FraudAlerts';
import {
  FuelDashboard, VehicleMaster, EquipmentMaster, RefuellingLog,
  TankStorage, FuelAlerts, DriverScores, FuelDriverList, FuelReports,
} from './pages/fuel/Fuel';
import GPSLiveMap from './pages/fuel/GPSLiveMap';
import MobilePettyCash from './pages/mobile/MobilePettyCash';
import MobileTravelRequest from './pages/mobile/MobileTravelRequest';
import MobileConsultantInvoice from './pages/mobile/MobileConsultantInvoice';
import MobileBD from './pages/mobile/MobileBD';
import MobileTendering from './pages/mobile/MobileTendering';import MobilePettyCashHome from './pages/mobile/MobilePettyCashHome';
import MobilePettyCashList from './pages/mobile/MobilePettyCashList';
import MobilePettyCashApprovals from './pages/mobile/MobilePettyCashApprovals';
import MobileCashAdvance from './pages/mobile/MobileCashAdvance';
import MobileFuelHome from './pages/mobile/MobileFuelHome';
import MobileFuelLog from './pages/mobile/MobileFuelLog';
import MobileFuelVehicles from './pages/mobile/MobileFuelVehicles';
import DPR from './pages/DPR';
import Finance from './pages/Finance';
import { ClientBillsPage } from './pages/finance/ClientBills';
import IT from './pages/IT';
import Admin from './pages/Admin';
import FinancialOverview from './pages/finance/FinancialOverview';
import ProjectPnL from './pages/finance/ProjectPnL';
import GSTRegister from './pages/finance/GSTRegister';
import TDSRegister from './pages/finance/TDSRegister';
import TallyExport from './pages/finance/TallyExport';
import TallyMapping from './pages/finance/TallyMapping';
import Payments from './pages/finance/Payments';
import EPCLedgers from './pages/finance/EPCLedgers';
import BGRegister from './pages/finance/BGRegister';
import TDSCenter from './pages/finance/TDSCenter';
import GSTCenter from './pages/finance/GSTCenter';
import CFOPack from './pages/finance/CFOPack';
import ShadowGL from './pages/finance/ShadowGL';
import QualityDashboard from './pages/quality/QualityDashboard';
import CubeTestList from './pages/quality/CubeTestList';
import CubeTestRecord from './pages/quality/CubeTestRecord';
import CubeTestDetail from './pages/quality/CubeTestDetail';
import NCRList from './pages/quality/NCRList';
import NCRDetail from './pages/quality/NCRDetail';
import RaiseNCR from './pages/quality/RaiseNCR';
import QualityHeatmap from './pages/quality/QualityHeatmap';
import InspectionRequests from './pages/quality/InspectionRequests';
import RaiseInspection from './pages/quality/RaiseInspection';
import ITPList from './pages/quality/ITPList';
import InspectionRequestDetail from './pages/quality/InspectionRequestDetail';
import Snags from './pages/quality/Snags';
import RaiseSnag from './pages/quality/RaiseSnag';
import MTCRegister from './pages/quality/MTCRegister';
import CAPAEffectiveness from './pages/quality/CAPAEffectiveness';
import EOTClaimPack from './pages/quality/EOTClaimPack';
import Subcontractors from './pages/Subcontractors';
import SubcontractorApprovals from './pages/SubcontractorApprovals';
import Engineering from './pages/Engineering';
import Tasks from './pages/Tasks';
import Labour from './pages/Labour';
import AskERP from './pages/AskERP';
import Risks from './pages/Risks';
import Inventory from './pages/Inventory';
import Equipment from './pages/Equipment';
import Stores from './pages/Stores';
import MRP from './pages/MRP';
import MaterialGaps from './pages/MaterialGaps';
import {
  HSE, Documents, Billing
} from './pages/Modules';
import CRM from './pages/CRM';
import HR from './pages/HR';
import UserManagement from './pages/UserManagement';
import Estimation from './pages/Estimation';
import Contracts from './pages/Contracts';
import Closeout from './pages/Closeout';
import MyTasks from './pages/crm/MyTasks';
import Profile from './pages/Profile';
import CompanyProfile from './pages/settings/CompanyProfile';
import ApprovalWorkflows from './pages/settings/ApprovalWorkflows';
import NumberSeries from './pages/settings/NumberSeries';
// NotificationsSettings import removed (Phase 0)
import AuditLog from './pages/settings/AuditLog';
import CRMSLA from './pages/settings/CRMSLA';
import EmailSettings from './pages/settings/EmailSettings';
import UploadLimitsSettings from './pages/settings/UploadLimits';
import NotificationPreferences from './pages/settings/NotificationPreferences';
import DataExport from './pages/settings/DataExport';
import OfficeLocations from './pages/settings/OfficeLocations';
import PettyCashCategories from './pages/settings/PettyCashCategories';
import DeletionRequests from './pages/settings/DeletionRequests';
import Backups from './pages/settings/Backups';
import EmailFromMap from './pages/settings/EmailFromMap';
import EmailDiagnostics from './pages/settings/EmailDiagnostics';
import EmailSubscriptions from './pages/settings/EmailSubscriptions';
import NotificationSubscriptions from './pages/settings/NotificationSubscriptions';
import RolesAccessControl from './pages/settings/RolesAccessControl';
import AIConfiguration from './pages/settings/AIConfiguration';
import UserAnalytics from './pages/settings/UserAnalytics';
import GPSTracking from './pages/settings/GPSTracking';
import FuelRCLookup from './pages/admin/FuelRCLookup';
import AppVersion from './pages/admin/AppVersion';
import SystemHealth from './pages/settings/SystemHealth';
import AccessControlSettings from './pages/settings/AccessControlSettings';
import AnalyticsBI from './pages/settings/AnalyticsBI';
import CompanyManagement from './pages/settings/CompanyManagement';
import GoogleDriveSettings from './pages/settings/GoogleDriveSettings';
import SecurityCenter from './pages/settings/SecurityCenter';
import FormFieldConfig from './pages/settings/FormFieldConfig';
import MasterListsRich from './pages/settings/MasterListsRich';
import OnboardingSettings from './pages/settings/OnboardingSettings';
import ITOnboardingChecklist from './pages/settings/ITOnboardingChecklist';
import StoresConfig from './pages/settings/StoresConfig';
import TCLibrary from './pages/settings/TCLibrary';
import StrategyReport from './pages/StrategyReport';
import CostControl from './pages/CostControl';
import DependencyInbox from './pages/tasks/DependencyInbox';
import TeamTaskReport from './pages/tasks/TeamTaskReport';
import MyAttendance from './pages/mobile/MyAttendance';
import KioskAttendance from './pages/mobile/KioskAttendance';
import FaceEnrollment from './pages/mobile/FaceEnrollment';
import LabourFaceEnroll from './pages/mobile/LabourFaceEnroll';
import MobileHome from './pages/mobile/MobileHome';
import MobileSiteVisit from './pages/mobile/MobileSiteVisit';
import MobileVisitorCheckin from './pages/mobile/MobileVisitorCheckin';
import MobileLabourRegister from './pages/mobile/MobileLabourRegister';
import LabourPerformance from './pages/mobile/LabourPerformance';
import MobileDPR from './pages/mobile/MobileDPR';
import MobileSyncStatus from './pages/mobile/MobileSyncStatus';
import MobileGRN from './pages/mobile/MobileGRN';
import MobileMR from './pages/mobile/MobileMR';
import MobileTransfers from './pages/mobile/MobileTransfers';
import MobileMaterialIssue from './pages/mobile/MobileMaterialIssue';
import MobileMaterialReturn from './pages/mobile/MobileMaterialReturn';
import MobileStocktake from './pages/mobile/MobileStocktake';
import MobileLeave from './pages/mobile/MobileLeave';
import MobileLayout from './components/MobileLayout';
import LabourMaster from './pages/LabourMaster';
import SupervisorPerformance from './pages/labour/SupervisorPerformance';
import LabourDashboard from './pages/LabourDashboard';
import KioskSecurity from './pages/KioskSecurity';
import PrivacyPolicy from './pages/PrivacyPolicy';
import AccountDeletion from './pages/AccountDeletion';
import InstallApp from './pages/InstallApp';
import NotFound from './pages/NotFound';
import VendorPortalOverview from './pages/admin/vendor-portal/VendorPortalOverview';
import VendorAccountsAdmin from './pages/admin/vendor-portal/VendorAccountsAdmin';
import VendorAuctionsAdmin from './pages/admin/vendor-portal/VendorAuctionsAdmin';
import VendorPortalDiagnostics from './pages/admin/vendor-portal/VendorPortalDiagnostics';
import VendorPortalSettingsPage from './pages/admin/vendor-portal/VendorPortalSettingsPage';
import PublicLanding from './pages/public/PublicLanding';
import PartnerLanding from './pages/public/PartnerLanding';
import { VendorAuthProvider } from './contexts/VendorAuthContext';
import VendorLogin from './pages/partner/VendorLogin';
import VendorRegister from './pages/partner/VendorRegister';
import VendorVerify from './pages/partner/VendorVerify';
import VendorApplication from './pages/partner/VendorApplication';
import VendorForgotPassword from './pages/partner/VendorForgotPassword';
import VendorResetPassword from './pages/partner/VendorResetPassword';
import VendorPortalLayout from './pages/partner/VendorPortalLayout';
import CommitteeDecidePage from './pages/CommitteeDecidePage';
import VendorDashboard from './pages/partner/VendorDashboard';
import VendorRFQs from './pages/partner/VendorRFQs';
import VendorInvoices from './pages/partner/VendorInvoices';
import VendorAuctions from './pages/partner/VendorAuctions';
import VendorProfile from './pages/partner/VendorProfile';
import VendorLedger from './pages/partner/VendorLedger';
import VendorPerformance from './pages/partner/VendorPerformance';
import VendorApplicationsReview from './pages/procurement/VendorApplicationsReview';
import LabourShiftConfig from './pages/admin/LabourShiftConfig';
import AttendanceHoursSettings from './pages/hr/AttendanceSettings';
import SalarySheetMapping from './pages/hr/SalarySheetMapping';
import LabourPaymentRuns from './pages/hr/LabourPaymentRuns';
import ApprovalsHub from './pages/ApprovalsHub';
import MobileESS from './pages/mobile/MobileESS';
import MobileApprovals from './pages/mobile/MobileApprovals';

// Wrapped in its own class-based error boundary so a version-check crash
// never brings down an authenticated page. Renders null on any error.
class ErrorSafeUpdatePrompt extends React.Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  render() { return this.state.failed ? null : <UpdatePrompt />; }
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0f1a2e] text-white text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <>
      {children}
      <ErrorSafeUpdatePrompt />
    </>
  );
}

// Hard route guard — blocks direct URL access to /app/vendor-portal-admin/* for
// any non-super_admin (defence-in-depth on top of the sidebar visibility filter).
function RequireSuperAdmin({ children }) {
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  const allowed = user?.allowed_modules;
  const allAccess = Array.isArray(allowed) && allowed.includes('*');
  const isSuper = role === 'super_admin' || allAccess;
  if (!isSuper) return <Navigate to="/app" replace />;
  return children;
}

// Landing for "/" — executives land on the Executive Dashboard; everyone else
// is routed to their self-service home (My Tasks) so non-exec users never hit
// the restricted dashboard (which renders empty/broken for them).
const EXEC_ROLES = ['md', 'ceo', 'cfo', 'super_admin'];
function HomeLanding() {
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  const dept = (user?.department || '').toLowerCase();
  const allowed = user?.allowed_modules;
  const allAccess = Array.isArray(allowed) && allowed.includes('*');
  const isSuper = role === 'super_admin' || allAccess;
  const moduleOk = isSuper || !Array.isArray(allowed) || allowed.includes('dashboard');
  const roleOk = isSuper || EXEC_ROLES.includes(role) || EXEC_ROLES.includes(dept);
  if (!(isSuper || (moduleOk && roleOk))) return <Navigate to="/app/my-tasks" replace />;
  return <Dashboard />;
}

function MobileAutoRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem('skip_mobile_redirect')) return;
    const path = location.pathname;

    // Capacitor native app — redirect away from public landing page.
    // With server.url = "https://elloraepc.in" the app opens at "/" which shows
    // the public site. Staff need to land on /login or /m (if authenticated).
    const isNative = (() => {
      try { return window.Capacitor?.isNativePlatform?.() || false; } catch { return false; }
    })();
    if (isNative && path === '/') {
      if (!loading) navigate(user ? '/m' : '/login', { replace: true });
      return;
    }

    // Round 4 P7 — proper device detection (touch + mobile UA), not just
    // viewport width. Previously a narrow browser window on desktop would
    // trigger the redirect, and a landscape tablet slightly wider than 768
    // would miss it. `deviceDetect` folds in the UA + touch-capability +
    // orientation heuristics so the redirect only fires for real phones and
    // small tablets.
    const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(navigator.userAgent || '');
    const isNarrow = window.innerWidth < 768;
    // A device counts as mobile if:
    //   • it is a native Capacitor app (already handled above), OR
    //   • it self-identifies as a mobile UA AND has touch capability, OR
    //   • it has touch AND a narrow viewport (small tablet)
    const isMobileDevice = isNative || (isMobileUA && isTouchDevice) || (isTouchDevice && isNarrow);
    if (!isMobileDevice) return;

    // Public routes — never auto-redirect to mobile app
    if (path === '/' || path === '/partner' ||
        path.startsWith('/m') || path.startsWith('/login') ||
        path === '/strategy-report' || path.startsWith('/privacy') ||
        path === '/account-deletion' || path === '/delete-account' ||
        path === '/install' || path === '/download' || path === '/get-app') return;
    // Only redirect authenticated staff hitting /app on mobile → /m
    // Preserve the intended path in sessionStorage so /m/home can return the
    // user to what they were trying to see (P7 spec: "preserving intended path").
    if (user && path.startsWith('/app')) {
      try {
        sessionStorage.setItem('intended_path', path + (location.search || ''));
      } catch {}
      navigate('/m', { replace: true });
    }
    // eslint-disable-next-line
  }, [location.pathname, user, loading]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // Legacy OAuth callback path — no longer used but redirect to login if hit
  if (location.hash?.includes('session_id=')) {
    window.history.replaceState(null, '', '/login');
    return <Login />;
  }
  return (
    <>
      {/* Status-bar fill — web/PWA fallback only. On the NATIVE Android app the
          status bar is handled natively (adjustMarginsForEdgeToEdge="auto" +
          navy windowBackground), where this resolves to 0px. On the web/PWA it
          paints a navy strip in the notch area on devices that report a real
          env(safe-area-inset-top). */}
      <div
        aria-hidden="true"
        data-testid="status-bar-fill"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 'env(safe-area-inset-top)',
          background: '#3b4872',
          zIndex: 2147483647,
          pointerEvents: 'none',
        }}
      />
      <MobileAutoRedirect />
      <Routes>
      {/* ── PUBLIC routes — no auth required ── */}
      <Route path="/" element={<PublicLanding />} />
      <Route path="/partner" element={<PartnerLanding />} />
      {/* ── VENDOR PORTAL — /partner/* (separate vendor auth) ── */}
      <Route path="/partner/login" element={<VendorLogin />} />
      <Route path="/partner/register" element={<VendorRegister />} />
      <Route path="/partner/verify" element={<VendorVerify />} />
      <Route path="/partner/forgot-password" element={<VendorForgotPassword />} />
      <Route path="/partner/reset-password" element={<VendorResetPassword />} />
      <Route path="/partner/application" element={<VendorApplication />} />
      {/* Committee Email-Scoped Access — no login required, token-authenticated */}
      <Route path="/committee/decide/:token" element={<CommitteeDecidePage />} />
      <Route path="/partner/dashboard" element={<VendorPortalLayout />}>
        <Route index element={<VendorDashboard />} />
        <Route path="rfqs" element={<VendorRFQs />} />
        <Route path="invoices" element={<VendorInvoices />} />
        <Route path="auctions" element={<VendorAuctions />} />
        <Route path="profile" element={<VendorProfile />} />
        <Route path="ledger" element={<VendorLedger />} />
        <Route path="performance" element={<VendorPerformance />} />
      </Route>
      <Route path="/login" element={<Login />} />
      <Route path="/pick-company" element={<CompanyPicker />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/account-deletion" element={<AccountDeletion />} />
      <Route path="/delete-account" element={<AccountDeletion />} />
      <Route path="/strategy-report" element={<StrategyReport />} />
      <Route path="/install" element={<InstallApp />} />
      <Route path="/download" element={<InstallApp />} />
      <Route path="/get-app" element={<InstallApp />} />
      {/* Mobile PWA — full-screen, with bottom tab-bar (no Layout chrome) */}
      <Route path="/m" element={<RequireAuth><MobileLayout /></RequireAuth>}>
        <Route index element={<MobileHome />} />
        <Route path="my-tasks" element={<MyTasks />} />
        <Route path="attend" element={<MyAttendance />} />
        <Route path="labour" element={<KioskAttendance />} />
        <Route path="labour-register" element={<MobileLabourRegister />} />
        <Route path="labour-performance" element={<LabourPerformance />} />
        <Route path="dpr" element={<MobileDPR />} />
        <Route path="sync" element={<MobileSyncStatus />} />
        <Route path="grn" element={<MobileGRN />} />
        <Route path="mr" element={<MobileMR />} />
        <Route path="transfers" element={<MobileTransfers />} />
        <Route path="issue" element={<MobileMaterialIssue />} />
        <Route path="return" element={<MobileMaterialReturn />} />
        <Route path="stocktake" element={<MobileStocktake />} />
        <Route path="leave" element={<MobileLeave />} />
        <Route path="ess" element={<MobileESS />} />
        <Route path="approvals" element={<MobileApprovals />} />
        <Route path="petty-cash" element={<MobilePettyCashHome />} />
        <Route path="petty-cash/expense" element={<MobilePettyCash />} />
        <Route path="petty-cash/list" element={<MobilePettyCashList />} />
        <Route path="petty-cash/approvals" element={<MobilePettyCashApprovals />} />
        <Route path="cash-advance" element={<MobileCashAdvance />} />
        <Route path="travel-request" element={<MobileTravelRequest />} />
        <Route path="consultant-invoice" element={<MobileConsultantInvoice />} />
        <Route path="bd" element={<MobileBD />} />
        <Route path="tendering" element={<MobileTendering />} />
        <Route path="fuel" element={<MobileFuelHome />} />
        <Route path="fuel/log" element={<MobileFuelLog />} />
        <Route path="fuel/vehicles" element={<MobileFuelVehicles />} />
        <Route path="enroll" element={<FaceEnrollment />} />
        <Route path="labour/enroll" element={<LabourFaceEnroll />} />
        <Route path="site-visit" element={<MobileSiteVisit />} />
        <Route path="visitor" element={<MobileVisitorCheckin />} />
        <Route path="safety" element={<MobileSafetyHome />} />
        <Route path="safety/observation" element={<RaiseObservation />} />
        <Route path="safety/near-miss" element={<RaiseNearMiss />} />
        <Route path="safety/toolbox" element={<ToolboxTalks />} />
        <Route path="safety/ppe" element={<PPECompliance />} />
        <Route path="quality" element={<MobileQualityHome />} />
        <Route path="quality/cube-test" element={<CubeTestRecord />} />
        <Route path="quality/ncr" element={<RaiseNCR />} />
        <Route path="quality/inspection" element={<RaiseInspection />} />
        <Route path="quality/snag" element={<RaiseSnag />} />
        <Route path="*" element={<NotFound />} />
      </Route>
      {/* ── STAFF ERP — all under /app, behind Layout auth guard ── */}
      <Route path="/app" element={<RouteGuard><Layout /></RouteGuard>}>
        <Route index element={<HomeLanding />} />
        <Route path="approvals" element={<ApprovalsHub />} />
        <Route path="my-tasks" element={<MyTasks />} />
        <Route path="crm/*" element={<CRM />} />
        <Route path="projects" element={<Projects />} />
        <Route path="projects/gantt" element={<Navigate to="/app/projects" replace />} />
        <Route path="projects/hindrances" element={<HindranceRegister />} />
        <Route path="projects/approvals-tracker" element={<ApprovalsTracker />} />
        <Route path="projects/primavera" element={<PrimaveraSync />} />
        <Route path="safety" element={<SafetyDashboard />} />
        <Route path="safety/raise-observation" element={<RaiseObservation />} />
        <Route path="safety/raise-near-miss" element={<RaiseNearMiss />} />
        <Route path="safety/observations/:id" element={<ObservationDetail />} />
        <Route path="safety/near-miss/:id" element={<NearMissDetail />} />
        <Route path="safety/toolbox-talks" element={<ToolboxTalks />} />
        <Route path="safety/ppe-compliance" element={<PPECompliance />} />
        <Route path="safety/permits" element={<PermitToWork />} />
        <Route path="hr/labour/payment-runs" element={<LabourPaymentRuns />} />
        <Route path="settings/labour-shift" element={<LabourShiftConfig />} />
        <Route path="settings/attendance-hours" element={<AttendanceHoursSettings />} />
        <Route path="settings/salary-sheet-mapping" element={<SalarySheetMapping />} />
        {/* Compliance Tracker (HR) */}
        <Route path="hr/compliance" element={<ComplianceDashboard />} />
        <Route path="hr/compliance/insurance" element={<ComplianceInsurance />} />
        <Route path="hr/compliance/statutory" element={<ComplianceStatutory />} />
        <Route path="hr/compliance/vault" element={<ComplianceVault />} />
        {/* Recruitment */}
        <Route path="hr/recruitment" element={<RecruitmentDashboard />} />
        <Route path="hr/attendance-report" element={<AttendanceReport />} />
        <Route path="hr/recruitment/jobs" element={<JobOpenings />} />
        <Route path="hr/recruitment/jobs/:jobId" element={<JobDetail />} />
        <Route path="hr/recruitment/candidates/:candidateId" element={<CandidateDetail />} />
        <Route path="hr/recruitment/board" element={<JobBoard />} />
        <Route path="hr/recruitment/pool" element={<CandidatePool />} />
        <Route path="hr/recruitment/scanner" element={<ResumeScanner />} />
        <Route path="hr/recruitment/matches" element={<JobMatches />} />
        <Route path="project-planning" element={<ProjectPlanningList />} />
        <Route path="project-planning/:id" element={<ProjectPlanningDetail />} />
        <Route path="projects/:id" element={<ProjectDetail />} />
        <Route path="risks" element={<Risks />} />
        <Route path="engineering" element={<Engineering />} />
        <Route path="procurement" element={<Procurement />} />
        <Route path="procurement/daily-report" element={<ProcurementDailyReport />} />
        <Route path="procurement/material-master" element={<MaterialMaster />} />
        <Route path="procurement/vendors" element={<VendorMaster />} />
        <Route path="procurement/vendor-applications" element={<VendorApplicationsReview />} />
        <Route path="procurement/vendor-change-requests" element={<VendorChangeRequests />} />
        <Route path="procurement/vendor-performance" element={<VendorPerformancePage />} />
        <Route path="procurement/work-orders" element={<WorkOrders />} />
        <Route path="procurement/work-orders/:id" element={<WorkOrderDetail />} />
        {/* Phase 3 — Petty Cash */}
        <Route path="petty-cash" element={<PettyCashDashboard />} />
        <Route path="petty-cash/expense-request" element={<ExpenseRequest />} />
        <Route path="petty-cash/bill-scanner" element={<BillScanner />} />
        <Route path="petty-cash/approvals" element={<ApprovalsQueue />} />
        <Route path="petty-cash/finance-queue" element={<FinanceProcessingQueue />} />
        <Route path="petty-cash/closures" element={<DailyCashClosure />} />
        <Route path="petty-cash/vendors" element={<VendorIntelligence />} />
        <Route path="petty-cash/alerts" element={<FraudAlerts />} />
        <Route path="petty-cash/cash-advances" element={<CashAdvances />} />
        <Route path="overview/fraud-alerts" element={<OverviewFraudAlerts />} />
        <Route path="petty-cash/reports" element={<PettyCashReports />} />
        {/* Phase 3 — Fuel Management */}
        <Route path="fuel" element={<FuelDashboard />} />
        <Route path="fuel/vehicles" element={<VehicleMaster />} />
        <Route path="fuel/gps-live" element={<GPSLiveMap />} />
        <Route path="fuel/equipment" element={<EquipmentMaster />} />
        <Route path="fuel/refuelling-log" element={<RefuellingLog />} />
        <Route path="fuel/tanks" element={<TankStorage />} />
        <Route path="fuel/alerts" element={<FuelAlerts />} />
        <Route path="fuel/driver-scores" element={<DriverScores />} />
        <Route path="fuel/drivers" element={<FuelDriverList />} />
        <Route path="fuel/reports" element={<FuelReports />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="stock-register" element={<Inventory />} />
        <Route path="equipment" element={<Equipment />} />
        <Route path="stores" element={<Stores />} />
        <Route path="mrp" element={<MRP />} />
        <Route path="material-gaps" element={<MaterialGaps />} />
        <Route path="subcontractors" element={<Subcontractors />} />
        <Route path="labour/subcontractor-approvals" element={<SubcontractorApprovals />} />
        <Route path="dpr/*" element={<DPR />} />
        <Route path="labour" element={<Labour />} />
        <Route path="labour-master" element={<LabourMaster />} />
        <Route path="labour-master/supervisor-performance" element={<SupervisorPerformance />} />
        <Route path="labour-dashboard" element={<LabourDashboard />} />
        <Route path="kiosk-security" element={<KioskSecurity />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="tasks/inbox" element={<DependencyInbox />} />
        <Route path="tasks/report" element={<TeamTaskReport />} />
        <Route path="cost-control" element={<CostControl />} />
        <Route path="ask-erp" element={<AskERP />} />
        <Route path="quality" element={<QualityDashboard />} />
        <Route path="quality/cube-tests" element={<CubeTestList />} />
        <Route path="quality/cube-tests/new" element={<CubeTestRecord />} />
        <Route path="quality/cube-tests/:id" element={<CubeTestDetail />} />
        <Route path="quality/ncrs" element={<NCRList />} />
        <Route path="quality/ncrs/new" element={<RaiseNCR />} />
        <Route path="quality/ncrs/:id" element={<NCRDetail />} />
        <Route path="quality/heatmap" element={<QualityHeatmap />} />
        <Route path="quality/inspection-requests" element={<InspectionRequests />} />
        <Route path="quality/inspection-requests/new" element={<RaiseInspection />} />
        <Route path="quality/inspection-requests/:id" element={<InspectionRequestDetail />} />
        <Route path="quality/itps" element={<ITPList />} />
        <Route path="quality/snags" element={<Snags />} />
        <Route path="quality/snags/new" element={<RaiseSnag />} />
        <Route path="quality/mtc-register" element={<MTCRegister />} />
        <Route path="quality/capa-effectiveness" element={<CAPAEffectiveness />} />
        <Route path="projects/eot-claim-pack" element={<EOTClaimPack />} />
        <Route path="hse" element={<HSE />} />
        <Route path="finance" element={<FinancialOverview />} />
        <Route path="finance/vendor-invoices" element={<VendorInvoicesPage />} />
        <Route path="finance/overview" element={<FinancialOverview />} />
        <Route path="finance/project-pnl" element={<ProjectPnL />} />
        <Route path="finance/gst-register" element={<GSTRegister />} />
        <Route path="finance/tds-register" element={<TDSRegister />} />
        <Route path="finance/tally-export" element={<TallyExport />} />
        <Route path="finance/tally-mapping" element={<TallyMapping />} />
        <Route path="finance/payments" element={<Payments />} />
        <Route path="finance/epc-ledgers" element={<EPCLedgers />} />
        <Route path="finance/bg-register" element={<BGRegister />} />
        <Route path="finance/tds" element={<TDSCenter />} />
        <Route path="finance/gst" element={<GSTCenter />} />
        <Route path="finance/cfo" element={<CFOPack />} />
        <Route path="finance/gl" element={<ShadowGL />} />
        <Route path="finance/cfo-dashboard" element={<Finance />} />
        <Route path="finance/client-bills" element={<ClientBillsPage />} />
        <Route path="hr/*" element={<HR />} />
        <Route path="it/*" element={<IT />} />
        <Route path="admin/*" element={<Admin />} />
        <Route path="estimation/*" element={<Estimation />} />
        <Route path="documents" element={<Documents />} />
        <Route path="billing" element={<Billing />} />
        <Route path="contracts" element={<Contracts />} />
        <Route path="closeout" element={<Closeout />} />
        <Route path="reports/*" element={<Reports />} />
        <Route path="mis" element={<ExecDashboardMIS />} />
        <Route path="mis/compare" element={<CompareProjectsMIS />} />
        <Route path="mis/reports" element={<ReportBuilderMIS />} />
        <Route path="reports/builder" element={<ReportBuilder />} />
        <Route path="mis/project/:id" element={<ProjectDeepDiveMIS />} />
        <Route path="crm/potential-vendors" element={<Navigate to="/app/estimation/potential-vendors" replace />} />
        <Route path="tendering/potential-vendors" element={<Navigate to="/app/estimation/potential-vendors" replace />} />
        <Route path="settings" element={<Settings />} />
        <Route path="profile" element={<Profile />} />
        <Route path="settings/company-profile" element={<CompanyProfile />} />
        <Route path="settings/office-locations" element={<OfficeLocations />} />
        <Route path="settings/petty-cash-categories" element={<PettyCashCategories />} />
        <Route path="settings/approval-workflows" element={<ApprovalWorkflows />} />
        <Route path="settings/number-series" element={<NumberSeries />} />
        <Route path="settings/stores-config" element={<StoresConfig />} />
        <Route path="settings/tc-library" element={<TCLibrary />} />
        {/* Notifications.jsx route removed (Phase 0) */}
        {/* Settings_Insights_Audit_Fix Item 4 — merged into Data Export (import panel added there) */}
        <Route path="settings/data-backup" element={<Navigate to="/app/settings/data-export" replace />} />
        <Route path="settings/audit-log" element={<AuditLog />} />
        <Route path="settings/crm-sla" element={<CRMSLA />} />
        <Route path="settings/email" element={<EmailSettings />} />
        <Route path="settings/upload-limits" element={<UploadLimitsSettings />} />
        <Route path="settings/companies" element={<CompanyManagement />} />
        <Route path="settings/notification-preferences" element={<NotificationPreferences />} />
        <Route path="settings/data-export" element={<DataExport />} />
        <Route path="settings/deletion-requests" element={<DeletionRequests />} />
        <Route path="settings/backups" element={<Backups />} />
        <Route path="settings/email-from-map" element={<EmailFromMap />} />
        <Route path="settings/email-diagnostics" element={<EmailDiagnostics />} />
        <Route path="settings/email-subscriptions" element={<EmailSubscriptions />} />
        <Route path="settings/notification-subscriptions" element={<NotificationSubscriptions />} />
        <Route path="settings/roles" element={<RolesAccessControl />} />
        <Route path="settings/ai" element={<AIConfiguration />} />
        <Route path="settings/user-analytics" element={<UserAnalytics />} />
        <Route path="settings/drive" element={<GoogleDriveSettings />} />
        <Route path="settings/gps" element={<GPSTracking />} />
        <Route path="settings/fuel-rc-lookup" element={<FuelRCLookup />} />
        <Route path="settings/app-version" element={<AppVersion />} />
        <Route path="settings/system-health" element={<SystemHealth />} />
        <Route path="settings/access-control" element={<AccessControlSettings />} />
        <Route path="settings/analytics" element={<AnalyticsBI />} />
        <Route path="settings/security" element={<SecurityCenter />} />
        <Route path="settings/form-fields" element={<FormFieldConfig />} />
        <Route path="settings/master-lists" element={<MasterListsRich />} />
        <Route path="settings/onboarding" element={<OnboardingSettings />} />
        <Route path="settings/it-onboarding-checklist" element={<ITOnboardingChecklist />} />
        <Route path="user-management" element={<UserManagement />} />
        {/* Vendor Portal Admin — super_admin only, route-guarded */}
        <Route path="vendor-portal-admin" element={<RequireSuperAdmin><VendorPortalOverview /></RequireSuperAdmin>} />
        <Route path="vendor-portal-admin/accounts" element={<RequireSuperAdmin><VendorAccountsAdmin /></RequireSuperAdmin>} />
        <Route path="vendor-portal-admin/auctions" element={<RequireSuperAdmin><VendorAuctionsAdmin /></RequireSuperAdmin>} />
        <Route path="vendor-portal-admin/diagnostics" element={<RequireSuperAdmin><VendorPortalDiagnostics /></RequireSuperAdmin>} />
        <Route path="vendor-portal-admin/settings" element={<RequireSuperAdmin><VendorPortalSettingsPage /></RequireSuperAdmin>} />
        {/* Catch-all inside authenticated layout — shows 404/Coming Soon with sidebar intact */}
        <Route path="*" element={<NotFound />} />
      </Route>
      {/* Legacy bare-path fallback: old bookmarks that predate the /app/* routing
          split get transparently redirected. Keep this block in sync with any new
          top-level sections added to the /app layout. */}
      <Route path="/my-tasks" element={<Navigate to="/app/my-tasks" replace />} />
      <Route path="/approvals" element={<Navigate to="/app/approvals" replace />} />
      <Route path="/crm/*" element={<Navigate to={`/app/crm/${window.location.pathname.replace(/^\/crm\/?/, '')}`} replace />} />
      <Route path="/hr/*" element={<Navigate to={`/app/hr/${window.location.pathname.replace(/^\/hr\/?/, '')}`} replace />} />
      <Route path="/projects/*" element={<Navigate to={`/app/projects/${window.location.pathname.replace(/^\/projects\/?/, '')}`} replace />} />
      <Route path="/finance/*" element={<Navigate to={`/app/finance/${window.location.pathname.replace(/^\/finance\/?/, '')}`} replace />} />
      <Route path="/settings/*" element={<Navigate to={`/app/settings/${window.location.pathname.replace(/^\/settings\/?/, '')}`} replace />} />
      <Route path="/procurement/*" element={<Navigate to={`/app/procurement/${window.location.pathname.replace(/^\/procurement\/?/, '')}`} replace />} />
      <Route path="/stores/*" element={<Navigate to={`/app/stores/${window.location.pathname.replace(/^\/stores\/?/, '')}`} replace />} />
      <Route path="/petty-cash/*" element={<Navigate to={`/app/petty-cash/${window.location.pathname.replace(/^\/petty-cash\/?/, '')}`} replace />} />
      <Route path="/fuel/*" element={<Navigate to={`/app/fuel/${window.location.pathname.replace(/^\/fuel\/?/, '')}`} replace />} />
      <Route path="/equipment/*" element={<Navigate to={`/app/equipment/${window.location.pathname.replace(/^\/equipment\/?/, '')}`} replace />} />
      <Route path="/safety/*" element={<Navigate to={`/app/safety/${window.location.pathname.replace(/^\/safety\/?/, '')}`} replace />} />
      <Route path="/quality/*" element={<Navigate to={`/app/quality/${window.location.pathname.replace(/^\/quality\/?/, '')}`} replace />} />
      <Route path="/reports/*" element={<Navigate to={`/app/reports/${window.location.pathname.replace(/^\/reports\/?/, '')}`} replace />} />
      <Route path="/estimation/*" element={<Navigate to={`/app/estimation/${window.location.pathname.replace(/^\/estimation\/?/, '')}`} replace />} />
      <Route path="/user-management" element={<Navigate to="/app/user-management" replace />} />
      <Route path="/dpr" element={<Navigate to="/app/dpr" replace />} />
      <Route path="/labour/*" element={<Navigate to={`/app/labour/${window.location.pathname.replace(/^\/labour\/?/, '')}`} replace />} />
      <Route path="/admin/*" element={<Navigate to={`/app/admin/${window.location.pathname.replace(/^\/admin\/?/, '')}`} replace />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <VendorAuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors closeButton />
        </BrowserRouter>
      </VendorAuthProvider>
    </AuthProvider>
  );
}

export default App;
